import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import Highcharts from 'highcharts';
import { LazyOutlet } from 'design-system/atoms/spinner-suspense';

export interface ContextChartsType {
  zoomState?: {
    startTime: number;
    endTime: number;
    trigger: any;
  };
  setZoomState: (zoomState: ContextChartsType['zoomState']) => void;
}

export const ChartsContext = createContext<ContextChartsType | null>(null);

export function useContextCharts(): ContextChartsType {
  const context = useContext(ChartsContext);
  if (context === null) {
    throw new Error('Trying to use ChartsContext outside of its provider');
  }
  return context;
}

export default function ContextCharts() {
  const [zoomState, setZoomState] =
    useState<ContextChartsType['zoomState']>(undefined);
  const ctx = useMemo(
    () => ({ zoomState, setZoomState }),
    [zoomState, setZoomState]
  );

  useEffect(() => {
    Highcharts.charts.forEach((chart) => {
      if (chart && zoomState?.trigger !== chart) {
        const isChartZoomed =
          chart.xAxis[0].getExtremes().userMax !== undefined &&
          chart.xAxis[0].getExtremes().userMin !== undefined;
        if (!zoomState) {
          if (isChartZoomed) {
            chart.zoomOut();
            // work around to hide the reset zoom button
            // https://www.highcharts.com/forum/viewtopic.php?t=46196
            const zoomButtons = document.querySelectorAll(
              '.highcharts-button.highcharts-reset-zoom'
            );
            zoomButtons.forEach((zoomButton) => {
              zoomButton.setAttribute('style', 'display: none');
            });
          }
        } else {
          chart.xAxis[0].setExtremes(zoomState.startTime, zoomState.endTime);
          chart.showResetZoom();
        }
      }
    });
  }, [zoomState]);

  return (
    <ChartsContext.Provider value={ctx}>
      <LazyOutlet />
    </ChartsContext.Provider>
  );
}
